import React, { useState } from 'react';
import { DialogContent, DialogTitle } from '../shared/Dialog';
import { MainButton } from '../shared/MainButton';
import { OrderApi, Ticket } from '@/lib/OrderApi';
import { ImportInfo } from './ImportInfo';
import * as m from '@/paraglide/messages.js';

interface Props {
    orderId: string;
    ticket: Ticket;
    onSuccess: () => void;
    onCancel: () => void;
}

/**
 * The API answers a refusal with a stable reason code rather than a sentence, so the
 * customer reads this in their own language instead of the English domain message.
 * Anything unrecognised (or a transport failure) falls back to the generic line.
 */
function messageForReason(reason: string | null): string {
    switch (reason) {
        case 'recipient_paid':
            return m.ticket_return_error_paid();
        case 'ticket_scanned':
            return m.ticket_return_error_scanned();
        case 'ticket_not_available':
            return m.ticket_return_error_not_available();
        case 'ticket_not_found':
        case 'ticket_not_claimed':
        case 'not_your_claim':
        case 'order_not_found':
            return m.ticket_return_error_gone();
        default:
            return m.ticket_return_error_generic();
    }
}

/**
 * FAS-974 — confirm handing a received ticket back to the person who shared it.
 *
 * Deliberately a confirmation step rather than a one-tap action: the hand-back is
 * immediate and cannot be undone by the holder, and their own copy stops working the
 * moment it goes through.
 */
const ReturnTicketModal: React.FC<Props> = ({ orderId, ticket, onSuccess, onCancel }) => {
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Falls back to a generic noun so the sentence still reads if the parent order's
    // main booker has no name on it.
    const sharedBy = ticket.sharedByName ?? m.ticket_return_sharer_fallback();

    async function submit() {
        setSubmitting(true);
        setError(null);

        const api = new OrderApi(import.meta.env.VITE_API_URL);
        const result = await api.returnTicket(orderId, ticket.ticketId);

        if (!result.success) {
            setError(messageForReason(result.reason));
            setSubmitting(false);
            return;
        }

        onSuccess();
    }

    return (
        <DialogContent className="bg-[var(--shop-panelBackgroundColor)] text-[var(--shop-textColor)]">
            <DialogTitle className="text-center mx-auto">{m.ticket_return_title()}</DialogTitle>

            <ImportInfo>
                <ImportInfo.Item>{m.ticket_return_disclaimer({ name: sharedBy })}</ImportInfo.Item>
            </ImportInfo>

            {/* Self-contained colours: the surrounding panel is organizer-themed, so a bare
                text-red on --shop-panelBackgroundColor is not reliably readable. */}
            {error !== null && (
                <p
                    role="alert"
                    className="text-sm mt-2 rounded-md border border-red-200 bg-red-100 text-red-900 px-3 py-2"
                >
                    {error}
                </p>
            )}

            <div className="flex flex-col gap-2 mt-4">
                <MainButton onClick={submit} loading={submitting} disabled={submitting}>
                    {submitting ? m.ticket_returning() : m.ticket_return_confirm()}
                </MainButton>
                <button
                    type="button"
                    onClick={onCancel}
                    disabled={submitting}
                    className="text-sm underline hover:no-underline disabled:opacity-40"
                >
                    {m.ticket_return_cancel()}
                </button>
            </div>
        </DialogContent>
    );
};

export default ReturnTicketModal;
